import React, { useState } from 'react';
import { X, Building2, User, Mail, Lock, AlertCircle } from 'lucide-react';

export default function CreateCompanyModal({ onClose, onCreate }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    status: 'trial',
    createAdmin: true,
    adminUser: {
      fullName: '',
      email: '',
      password: ''
    }
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-generate slug from name
    if (field === 'name') {
      setFormData(prev => ({
        ...prev,
        slug: value?.toLowerCase()?.replace(/[^a-z0-9\s-]/g, '')?.replace(/\s+/g, '-')?.replace(/-+/g, '-')?.trim()
      }));
    }

    // Clear errors when user types
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAdminInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      adminUser: {
        ...prev?.adminUser,
        [field]: value
      }
    }));

    // Clear errors when user types
    if (errors?.[`admin.${field}`]) {
      setErrors(prev => ({ ...prev, [`admin.${field}`]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Company validation
    if (!formData?.name?.trim()) {
      newErrors.name = 'Company name is required';
    }
    if (!formData?.slug?.trim()) {
      newErrors.slug = 'Company slug is required';
    }

    // Admin user validation (if creating admin)
    if (formData?.createAdmin) {
      if (!formData?.adminUser?.fullName?.trim()) {
        newErrors['admin.fullName'] = 'Admin name is required';
      }
      if (!formData?.adminUser?.email?.trim()) {
        newErrors['admin.email'] = 'Admin email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.adminUser?.email)) {
        newErrors['admin.email'] = 'Invalid email format';
      }
      if (!formData?.adminUser?.password || formData?.adminUser?.password?.length < 6) {
        newErrors['admin.password'] = 'Password must be at least 6 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onCreate(formData);
    } catch (error) {
      console.error('Error creating company:', error);
      setErrors({ submit: 'Failed to create company. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Create New Company</h2>
              <p className="text-sm text-gray-500">Add a new company to the platform</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Company Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors?.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter company name"
                />
                {errors?.name && (
                  <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors?.name}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Slug *
                </label>
                <input
                  type="text"
                  value={formData?.slug}
                  onChange={(e) => handleInputChange('slug', e?.target?.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors?.slug ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="company-slug"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Used in URLs and must be unique
                </p>
                {errors?.slug && (
                  <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors?.slug}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Initial Status
                </label>
                <select
                  value={formData?.status}
                  onChange={(e) => handleInputChange('status', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                </select>
              </div>
            </div>
          </div>

          {/* Admin User Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Administrator Account</h3>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData?.createAdmin}
                  onChange={(e) => handleInputChange('createAdmin', e?.target?.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Create admin user</span>
              </label>
            </div>

            {formData?.createAdmin && (
              <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData?.adminUser?.fullName}
                    onChange={(e) => handleAdminInputChange('fullName', e?.target?.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors?.['admin.fullName'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter admin full name"
                  />
                  {errors?.['admin.fullName'] && (
                    <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors?.['admin.fullName']}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData?.adminUser?.email}
                    onChange={(e) => handleAdminInputChange('email', e?.target?.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors?.['admin.email'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="admin@company.com"
                  />
                  {errors?.['admin.email'] && (
                    <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors?.['admin.email']}</span>
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="w-4 h-4 inline mr-1" />
                    Password *
                  </label>
                  <input
                    type="password"
                    value={formData?.adminUser?.password}
                    onChange={(e) => handleAdminInputChange('password', e?.target?.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors?.['admin.password'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter password"
                  />
                  {errors?.['admin.password'] && (
                    <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors?.['admin.password']}</span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Submit Error */}
          {errors?.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle className="w-4 h-4" />
                <span>{errors?.submit}</span>
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Creating...' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}